"use client";

import { useState, useEffect, useCallback } from "react";
import { CalendarDays, ChevronRight } from "lucide-react";
import Link from "next/link";
import { useI18n } from "@/i18n/I18nProvider";
import PageHeader from "@/components/layout/PageHeader";
import EmptyState from "@/components/ui/EmptyState";
import DemoNote from "@/components/ui/DemoNote";
import { getBookings, bookingStatusConfig } from "@/services/mockService";
import type { Booking } from "@/data/mockData";

export default function BookingsPage() {
  const { t } = useI18n();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    getBookings().then((data) => {
      setBookings(data);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    load();
    // Demo status changes are shared through the server store; refresh when the
    // tab becomes visible so a patient device picks up admin updates.
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(load, 20000);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, [load]);

  return (
    <div className="pb-24">
      <PageHeader title={t("My Bookings")} subtitle={t("Track your service requests")} />
      <DemoNote text={t("Demo booking notice")} />

      <div className="px-5 py-4 space-y-3">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => <div key={i} className="cc-skeleton h-[176px]" />)
        ) : bookings.length === 0 ? (
          <EmptyState
            icon={CalendarDays}
            title={t("No bookings yet")}
            hint={t("Submit a request and our team will coordinate your care.")}
            action={
              <Link href="/book" className="inline-flex items-center justify-center h-11 px-6 rounded-[14px] bg-primary text-white text-[14px] font-semibold active:bg-primary/90">
                {t("Book a Service")}
              </Link>
            }
          />
        ) : (
          bookings.map((booking) => {
            const statusCfg = bookingStatusConfig[booking.status];
            return (
              <div key={booking.id} className="cc-card p-4">
                <div className="flex items-start justify-between mb-2">
                  <span className="text-[12px] text-text-dark/40 font-mono">{booking.id}</span>
                  <span className="text-[11.5px] font-semibold px-2.5 py-1 rounded-full" style={{ color: statusCfg.color, backgroundColor: statusCfg.bgColor }}>
                    {t(statusCfg.label)}
                  </span>
                </div>
                <h3 className="text-[15.5px] font-bold text-text-dark mb-1">{booking.serviceTitle}</h3>
                <p className="text-[13.5px] text-text-dark/60 mb-0.5">{booking.hospitalName}</p>
                <p className="text-[12.5px] text-text-dark/45 mb-2.5">{booking.doctorName} &middot; {booking.preferredDate}</p>
                <p className="text-[13px] text-text-dark/55 bg-page-bg rounded-xl p-2.5 leading-[1.5]">{booking.statusNote}</p>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#EDF1F1]">
                  <span className="text-[12px] text-text-dark/35">{t("Submitted")} {booking.submittedAt}</span>
                  <ChevronRight size={15} className="text-text-dark/30" />
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
